import { useCitadel } from "@/hooks/use-citadel";
import type { ProjectTab } from "@/sites/projects/project/tabs";

/**
 * Which tabs of the project detail page this caller has no rule for.
 *
 * Three of the twelve show something a project read does not carry: the invoices booked against
 * the site, its measurements and its warehouse orders. An ordinary employee holds none of those
 * three rules. All three would answer 403 and toast an error on a page they were never meant to
 * see, so all three are dropped on the rule rather than on the response.
 *
 * The list is a hook rather than a computation at each site because two things have to agree about
 * it: `Project` renders the tabs, and `projectTabPath` turns a tab's name into the index `Tabs`
 * addresses panels by — an index over the panels that are actually rendered. Whoever links to a
 * tab passes this list, so a caller who cannot see the invoices tab still lands on the right one.
 *
 * The same three-state guard the `Can` component documents applies: a tab disappears only on a
 * definitive denial, so rules still loading keep it and let the server have the last word.
 */
export function useHiddenProjectTabs(): ProjectTab[] {
	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	const hidden: ProjectTab[] = [];
	if (citadel?.cannot("read", "Invoice")) hidden.push("invoices");
	if (citadel?.cannot("read", "Measurement")) hidden.push("measurements");
	if (citadel?.cannot("read", "Stock")) hidden.push("orders");

	/** Return */
	return hidden;
}
